import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Modal, Box, TextField, Select, MenuItem, Button, IconButton, FormControl,
  Typography, CircularProgress, Switch,
  Tooltip, Autocomplete, Radio, RadioGroup, FormControlLabel, Checkbox, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Pagination
} from '@mui/material';
import { debounce } from 'lodash';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SuccessScreen from '../SuccessScreen';
import ErrorManager from '../ErrorManager';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { fetchBots, fetchTagsPaginated } from '../../application/service/apiService';
import BrokerStepIndicator from './BrokerStepIndicator';
import ImproveTextWithAI from '../ImproveTextWithAI/ImproveTextWithAI';
import '../modalStyle.css';

const BrokerModal = ({
  open,
  onClose,
  channelId,
  channelName,
  isOperadorOnly: isOperadorOnlyProp,
  currentUserId: currentUserIdProp,
}) => {
  const { t } = useTranslation();
  const [usuarioSelecionado, setUsuarioSelecionado] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [schedule_datetime, setSchedule_datetime] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [mensagemMelhoradaPorIA, setMensagemMelhoradaPorIA] = useState(false);
  const [arquivoCsv, setArquivoCsv] = useState(null);
  const [arquivoCsvUrl, setArquivoCsvUrl] = useState('');
  const [imagem, setImagem] = useState(null);
  const [imagemUrl, setImagemUrl] = useState('');
  const [entryInternationalPhone, setEntryInternationalPhone] = useState(false);
  const [adicionarEtiqueta, setAdicionarEtiqueta] = useState(true);
  const [contatosDuplicados, setContatosDuplicados] = useState(true);
  const [fecharChat, setFecharChat] = useState(true);
  const [email, setEmail] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isCsvUploaded, setIsCsvUploaded] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isCsvDragActive, setIsCsvDragActive] = useState(false);
  const [isImageDragActive, setIsImageDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [etiqueta, setEtiqueta] = useState('');
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [tagsCurrentPage, setTagsCurrentPage] = useState(1);
  const [tagsLastPage, setTagsLastPage] = useState(1);
  const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [tagSearchInputValue, setTagSearchInputValue] = useState('');
  const [mensagemRapida, setMensagemRapida] = useState('');
  const [mensagensRapidas, setMensagensRapidas] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [templateCurrentPage, setTemplateCurrentPage] = useState(1);
  const [templateLastPage, setTemplateLastPage] = useState(1);
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  const [templateAutocompleteOpen, setTemplateAutocompleteOpen] = useState(false);
  
  // Estados para modal de erro
  const [errorData, setErrorData] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  // Estados para controle de roles do usuário (vindos das props ou locais)
  const [isOperadorOnly, setIsOperadorOnly] = useState(isOperadorOnlyProp || false);
  const [currentUserId, setCurrentUserId] = useState(currentUserIdProp || null);

  // Estados para Base por Filtro de Interação
  const [baseSelectionType, setBaseSelectionType] = useState('csv'); // 'csv' ou 'filter'
  const [filterNoInteractionDays, setFilterNoInteractionDays] = useState('15');
  const [filterNoInteractionCustom, setFilterNoInteractionCustom] = useState('');
  const [selectAllContacts, setSelectAllContacts] = useState(true);
  const [previewContacts, setPreviewContacts] = useState([]);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [previewTotalContacts, setPreviewTotalContacts] = useState(0);
  const [previewCurrentPage, setPreviewCurrentPage] = useState(1);
  const [previewLastPage, setPreviewLastPage] = useState(1);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeStep, setActiveStep] = useState(0);

  const { acontID, token } = useParams();
  const errorManagerRef = useRef(null);
  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Atualizar estados quando as props mudarem
  useEffect(() => {
    if (isOperadorOnlyProp !== undefined) {
      setIsOperadorOnly(isOperadorOnlyProp);
    }
    if (currentUserIdProp !== undefined) {
      setCurrentUserId(currentUserIdProp);
    }
  }, [isOperadorOnlyProp, currentUserIdProp]);

  const fetchUsuarios = useCallback(async () => {
    // Se for apenas operador, não precisa buscar lista de usuários
    if (!channelId || isOperadorOnly) return;

    try {
      let usuarios = [];
      let currentPage = 1;
      let lastPage = 1;

      const firstResponse = await fetch(`${process.env.REACT_APP_URL_DISP}/get-users/channels/${channelId}?perPage=150&page=${currentPage}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!firstResponse.ok) throw new Error('Erro ao buscar usuários');
      const firstData = await firstResponse.json();
      usuarios = firstData.data;
      lastPage = firstData.last_page;

      setUsuarios(usuarios);

      let allUsuarios = usuarios;

      while (currentPage < lastPage) {
        currentPage++;
        const response = await fetch(`${process.env.REACT_APP_URL_DISP}/get-users/channels/${channelId}?perPage=20&page=${currentPage}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Erro ao buscar usuários');
        const data = await response.json();
        allUsuarios = [...allUsuarios, ...data.data];
      }

      setUsuarios(allUsuarios);
    } catch (error) {
      console.error('Erro ao buscar os usuários:', error.message);
      errorManagerRef.current?.showError('Erro ao buscar os usuários');
    }
  }, [channelId, token, isOperadorOnly]);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const getTagColor = (tagColor) => tagColor || '#CCCCCC';

  const tagOptions = useMemo(() => {
    if (!isLoadingMoreTags) {
      return tags;
    }

    return [
      ...tags,
      {
        id: '__loading_more_tags__',
        name: 'Carregando mais tags...',
        isLoadingOption: true,
      },
    ];
  }, [tags, isLoadingMoreTags]);

  const renderTagOption = (props, option) => {
    if (option?.isLoadingOption) {
      return (
        <Box
          component="li"
          {...props}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 1,
          }}
        >
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {option?.name}
          </Typography>
        </Box>
      );
    }

    const tagColor = getTagColor(option?.color);

    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: tagColor,
            border: '1px solid rgba(0, 0, 0, 0.15)',
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {option?.name || ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  const selectedTagIds = useMemo(
    () => selectedTags.map((tag) => tag?.id).filter((id) => id !== null && id !== undefined),
    [selectedTags]
  );

  useEffect(() => {
    if (open) {
      const now = new Date();
      // Se for operador apenas, pré-seleciona o usuário diretamente
      if (isOperadorOnly && currentUserId) {
        setUsuarioSelecionado(currentUserId);
      } else {
        // Caso contrário, busca a lista de usuários
        fetchUsuarios();
      }
      setSchedule_datetime(formatDate(now));
    }
  }, [open, fetchUsuarios, isOperadorOnly, currentUserId]);

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL_DISP}/upload/customer/${acontID}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        return result.fileUrl;
      } else {
        const errorText = await response.text();
        errorManagerRef.current?.showError('Erro no upload do arquivo: ' + errorText);
        return null;
      }
    } catch (error) {
      errorManagerRef.current?.showError('Erro no upload do arquivo: ' + error.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    setIsImageLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL_DISP}/upload/customer/${acontID}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const result = await response.json();
        return result.fileUrl;
      } else {
        const errorText = await response.text();
        errorManagerRef.current?.showError('Erro no upload da imagem: ' + errorText);
        return null;
      }
    } catch (error) {
      errorManagerRef.current?.showError('Erro no upload da imagem: ' + error.message);
      return null;
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.type === 'text/csv' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setArquivoCsv(file);
        const url = await handleFileUpload(file);
        if (url) {
          setArquivoCsvUrl(url);
          setIsCsvUploaded(true);
          setFormErrors((prev) => ({ ...prev, csv: '' })); // Limpa o erro ao anexar um arquivo
          // Limpa dados de validação anterior quando novo arquivo é carregado
          setValidationData(null);
        }
      } else {
        errorManagerRef.current?.showError(t('validation.csvFormatInvalid'));
      }
    }
  };

  const handleImageChange = async (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const fileType = file.type;
      if (fileType === 'image/jpeg' || fileType === 'image/png' || fileType === 'image/gif' || fileType === 'application/pdf') {
        if (mensagemRapida) {
          setMensagemRapida('');
        }
        
        setImagem(file);
        const url = await handleImageUpload(file);
        if (url) {
          setImagemUrl(url);
          setIsImageUploaded(true);
        }
      } else {
        errorManagerRef.current?.showError(t('validation.imageFormatInvalid'));
      }
    }
  };

  const handleRemoveCsv = () => {
    setArquivoCsv(null);
    setArquivoCsvUrl('');
    setIsCsvUploaded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImagem(null);
    setImagemUrl('');
    setIsImageUploaded(false);
  };

  const handleCsvDragOver = (event) => {
    event.preventDefault();
    setIsCsvDragActive(true);
  };

  const handleCsvDragLeave = () => {
    setIsCsvDragActive(false);
  };

  const handleCsvDrop = async (event) => {
    event.preventDefault();
    setIsCsvDragActive(false);
    const file = event.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      setArquivoCsv(file);
      const url = await handleFileUpload(file);
      if (url) {
        setArquivoCsvUrl(url);
        setIsCsvUploaded(true);
        setFormErrors((prev) => ({ ...prev, csv: '' }));
        // Limpa dados de validação anterior quando novo arquivo é carregado
        setValidationData(null);
      }
    } else {
      errorManagerRef.current?.showError(t('validation.csvFormatInvalid'));
    }
  };

  const handleImageDragOver = (event) => {
    event.preventDefault();
    setIsImageDragActive(true);
  };

  const handleImageDragLeave = () => {
    setIsImageDragActive(false);
  };

  const handleImageDrop = async (event) => {
    event.preventDefault();
    setIsImageDragActive(false);
    const file = event.dataTransfer.files[0];
    const fileType = file.type;
    if (file && (fileType === 'image/jpeg' || fileType === 'image/png' || fileType === 'image/gif' || fileType === 'application/pdf')) {
      if (mensagemRapida) {
        setMensagemRapida('');
      }
      
      setImagem(file);
      const url = await handleImageUpload(file);
      if (url) {
        setImagemUrl(url);
        setIsImageUploaded(true);
      }
    } else {
      errorManagerRef.current?.showError(t('validation.imageFormatInvalid'));
    }
  };

  const validateFileContacts = async (csvUrl = null) => {
    setIsValidating(true);
    const urlToValidate = csvUrl || arquivoCsvUrl;
    
    if (!urlToValidate) {
      errorManagerRef.current?.showError(t('validation.csvRequired'));
      setIsValidating(false);
      return;
    }
    
    try {
      const formData = new URLSearchParams();
      formData.append('link_file', urlToValidate);
      formData.append('template', '');
      formData.append('quick_message_id', mensagemRapida || '');
      formData.append('international_phone', entryInternationalPhone.toString());
      formData.append('deduplicate_contact', contatosDuplicados.toString());
      formData.append('trigger_type', 'BROKER');

      const response = await fetch(
        `${process.env.REACT_APP_URL_DISP}/trigger/validate-file/BROKER/${acontID}/${channelId}/${usuarioSelecionado}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        setValidationData(data);
        setConfirmationModalOpen(true);
      } else {
        // Tratamento específico para erro 400 (CSV inválido)
        if (response.status === 400) {
          try {
            const errorData = await response.json();
            setErrorData(errorData);
            setErrorModalOpen(true);
            return;
          } catch (parseError) {
            throw new Error(t('validation.csvFileInvalid'));
          }
        } else {
          const errorText = await response.text();
          throw new Error(`Erro na validação: ${response.status} - ${errorText}`);
        }
      }
    } catch (error) {
      errorManagerRef.current?.showError(error.message || 'Erro ao validar arquivo. Tente novamente.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});

    const currentDate = new Date();
    const maxDate = new Date(currentDate);
    maxDate.setDate(maxDate.getDate() + 6);
    const selectedDate = new Date(schedule_datetime);

    // Acumula erros em um objeto temporário
    const errors = {};
    if (!isOperadorOnly && !usuarioSelecionado) errors.usuario = t('validation.userRequired');
    if (!schedule_datetime) errors.date = t('validation.dateRequired');
    if (!mensagem && !mensagemRapida) errors.mensagem = t('validation.messageRequired');
    
    // Validação específica para cada tipo de seleção de base
    if (baseSelectionType === 'csv') {
      if (!arquivoCsvUrl) errors.csv = t('validation.csvRequired');
    } else if (baseSelectionType === 'filter' || baseSelectionType === 'tag') {
      if (!selectAllContacts && selectedContactIds.length === 0) {
        errors.contacts = t('disparador.selecioneContatos') || 'Selecione pelo menos um contato';
      }
      if (baseSelectionType === 'tag' && selectedTagIds.length === 0) {
        errors.tag = t('disparador.selecioneTag') || 'Selecione uma tag';
      }
    }
    
    if (!email) {
      errors.email = t('validation.emailRequired');
    } else if (!validateEmail(email)) {
      errors.email = t('validation.emailInvalid');
    }
    if (selectedDate > maxDate) errors.date = t('validation.dateExceedsLimit');

    // Define os erros no estado
    setFormErrors(errors);

    // Se houver erros, interrompe o envio
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    // Se estiver usando filtro ou tag, gerar CSV primeiro
    let csvUrlToValidate = arquivoCsvUrl;
    if (baseSelectionType === 'filter' || baseSelectionType === 'tag') {
      const csvUrl = await generateCsvFromSelectedContacts();
      if (!csvUrl) {
        setIsSubmitting(false);
        return;
      }
      setArquivoCsvUrl(csvUrl);
      csvUrlToValidate = csvUrl;
    }

    // Sempre validar antes de enviar, passando a URL correta
    await validateFileContacts(csvUrlToValidate);
    setIsSubmitting(false);
  };

  const handleConfirmSend = async () => {
    setConfirmationModalOpen(false);
    setIsSubmitting(true);
    
    try {
      // Verificar se temos a URL do CSV
      if (!arquivoCsvUrl) {
        errorManagerRef.current?.showError(t('validation.csvRequired'));
        setIsSubmitting(false);
        return;
      }
      
      const currentDate = new Date();
      const maxDate = new Date(currentDate);
      maxDate.setDate(maxDate.getDate() + 6);
      const selectedDate = new Date(schedule_datetime);

      const formData = new FormData();
      if (mensagemRapida) {
        formData.append('quick_message_id', mensagemRapida);
      } else {
        formData.append('entry_msg', mensagem);
        formData.append('entry_link_image', imagemUrl || '');
      }
      formData.append('entry_chat_close', fecharChat ? 'true' : 'false');
      formData.append('entry_deduplicate_contact', contatosDuplicados ? 'true' : 'false');
      formData.append('entry_link_file', arquivoCsvUrl);
      formData.append('entry_email', email);
      formData.append('entry_create_tag', adicionarEtiqueta ? 'true' : 'false');
      formData.append('entry_international_phone', entryInternationalPhone ? 'true' : 'false');
      if (etiqueta) formData.append('entry_custom_tag_name', etiqueta);
      if (fecharChat) formData.append('entry_bot_legacy_id', selectedBot || '');

      // Adicionar campos de filtro de arquivo
      const getFileInputTypeLabel = (type) => {
        const typeMap = {
          'csv': t('disparador.fileTypeSpreadsheet'),
          'tag': t('disparador.fileTypeTag'),
          'filter': t('disparador.fileTypeFilter')
        };
        return typeMap[type] || type;
      };
      
      formData.append('entry_filter_file_type_input', getFileInputTypeLabel(baseSelectionType));
      if (baseSelectionType === 'tag' && selectedTags.length > 0) {
        formData.append('entry_filter_file_tag_id', selectedTags[0]?.id);
      }
      if (baseSelectionType === 'filter' && filterNoInteractionDays) {
        const lastMessageDate = new Date();
        const daysToSubtract = filterNoInteractionDays === 'custom'
          ? parseInt(filterNoInteractionCustom) || 0
          : parseInt(filterNoInteractionDays) || 0;
        lastMessageDate.setDate(lastMessageDate.getDate() - daysToSubtract);
        formData.append('entry_filter_file_date_interaction', formatDate(lastMessageDate));
      }

      if (selectedDate > currentDate) {
        formData.append('schedule_datetime', formatDate(selectedDate));
      }

      const response = await fetch(
        `${process.env.REACT_APP_URL_DISP}/trigger/BROKER/${acontID}/${channelId}/${usuarioSelecionado}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json',
            'Connection': 'keep-alive',
          },
          body: formData,
        }
      );

      if (response.ok) {
        setShowSuccessMessage(true);
      } else {
        const errorText = await response.text();
        errorManagerRef.current?.showError(`Erro na requisição: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      errorManagerRef.current?.showError('Erro ao enviar os dados: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChangeDynamic = async (event, value) => {
    if (value) {
      try {
        const response = await fetch(`${process.env.REACT_APP_URL_DISP}/get-users/channels/${channelId}?perPage=150&entry_user_name=${value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsuarios(data.data);
        } else {
          errorManagerRef.current?.showError('Erro ao buscar usuários.');
        }
      } catch (error) {
        errorManagerRef.current?.showError('Erro ao buscar usuários.');
      }
    } else {
      fetchUsuarios();
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const fetchBotsData = useCallback(async () => {
    try {
      const botsData = await fetchBots(acontID, channelId, token);
      setBots(botsData.data || []);
    } catch (error) {
      // Em caso de erro, não exibir toast, apenas manter array vazio
      setBots([]);
    }
  }, [acontID, channelId, token]);

  const fetchTagsData = useCallback(async (name = '') => {
    setIsTagsLoading(true);
    try {
      const tagsData = await fetchTagsPaginated(acontID, token, 1, 150, name);
      setTags(tagsData?.data || []);
      setTagsCurrentPage(tagsData?.current_page || 1);
      setTagsLastPage(tagsData?.last_page || 1);
    } catch (error) {
      setTags([]);
      setTagsCurrentPage(1);
      setTagsLastPage(1);
    } finally {
      setIsTagsLoading(false);
    }
  }, [acontID, token]);

  const handleLoadMoreTags = useCallback(async () => {
    if (isTagsLoading || isLoadingMoreTags || tagsCurrentPage >= tagsLastPage) {
      return;
    }

    setIsLoadingMoreTags(true);
    try {
      const nextPage = tagsCurrentPage + 1;
      const tagsData = await fetchTagsPaginated(acontID, token, nextPage, 150, tagSearchTerm);
      const nextTags = tagsData?.data || [];

      setTags((prevTags) => {
        const existingIds = new Set(prevTags.map((tag) => tag?.id));
        const dedupedNewTags = nextTags.filter((tag) => !existingIds.has(tag?.id));
        return [...prevTags, ...dedupedNewTags];
      });

      setTagsCurrentPage(tagsData?.current_page || nextPage);
      setTagsLastPage(tagsData?.last_page || tagsLastPage);
    } catch (error) {
      errorManagerRef.current?.showError('Erro ao carregar mais tags');
    } finally {
      setIsLoadingMoreTags(false);
    }
  }, [isTagsLoading, isLoadingMoreTags, tagsCurrentPage, tagsLastPage, acontID, token, tagSearchTerm]);

  const searchTags = useCallback(async (searchTerm) => {
    const normalizedTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';
    setTagSearchTerm(normalizedTerm);
    await fetchTagsData(normalizedTerm);
  }, [fetchTagsData]);

  const handleTagsListScroll = useCallback((event) => {
    const listboxNode = event.currentTarget;
    const reachedBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

    if (reachedBottom) {
      handleLoadMoreTags();
    }
  }, [handleLoadMoreTags]);

  // Função para buscar contatos por filtro (preview)
  const fetchContactsByFilter = useCallback(async (page = 1) => {
    setIsLoadingPreview(true);
    try {
      const requestBody = baseSelectionType === 'tag'
        ? {
          tag_ids: selectedTagIds,
          contact_ids: [],
          trigger_type: 'BROKER',
          search: searchQuery || '',
          get_url: false,
          perPage: 20,
          page,
        }
        : (() => {
          const lastMessageDate = new Date();
          const daysToSubtract = filterNoInteractionDays === 'custom'
            ? parseInt(filterNoInteractionCustom) || 0
            : parseInt(filterNoInteractionDays) || 0;
          lastMessageDate.setDate(lastMessageDate.getDate() - daysToSubtract);

          return {
            contact_ids: [],
            last_message_date: formatDate(lastMessageDate),
            trigger_type: 'BROKER',
            search: searchQuery || '',
            get_url: false,
            perPage: 20,
            page,
          };
        })();

      const response = await fetch(
        `${process.env.REACT_APP_URL_DISP}/contacts/${acontID}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar contatos');
      }

      const data = await response.json();
      setPreviewContacts(data.data || []);
      setPreviewTotalContacts(data.total || 0);
      setPreviewCurrentPage(data.current_page || 1);
      setPreviewLastPage(data.last_page || 1);
    } catch (error) {
      console.error('Erro ao buscar contatos:', error);
      errorManagerRef.current?.showError(t('disparador.erroCarregarContatos') || 'Erro ao carregar contatos');
      setPreviewContacts([]);
      setPreviewTotalContacts(0);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [baseSelectionType, selectedTagIds, acontID, token, filterNoInteractionDays, filterNoInteractionCustom, searchQuery, t]);

  // Função para gerar CSV com contatos selecionados
  const generateCsvFromSelectedContacts = useCallback(async () => {
    if (baseSelectionType === 'tag' && selectedTagIds.length === 0) {
      errorManagerRef.current?.showError(t('disparador.selecioneTag') || 'Selecione uma tag');
      return null;
    }

    if ((baseSelectionType === 'filter' || baseSelectionType === 'tag') && !selectAllContacts && selectedContactIds.length === 0) {
      errorManagerRef.current?.showError(t('disparador.selecioneContatos') || 'Selecione pelo menos um contato');
      return null;
    }

    try {
      const requestBody = baseSelectionType === 'tag'
        ? {
          tag_ids: selectedTagIds,
          contact_ids: selectAllContacts ? [] : selectedContactIds,
          trigger_type: 'BROKER',
          search: searchQuery || '',
          get_url: true,
          perPage: 20,
          page: 1,
        }
        : (() => {
          const lastMessageDate = new Date();
          const daysToSubtract = filterNoInteractionDays === 'custom'
            ? parseInt(filterNoInteractionCustom) || 0
            : parseInt(filterNoInteractionDays) || 0;
          lastMessageDate.setDate(lastMessageDate.getDate() - daysToSubtract);

          return {
            contact_ids: selectAllContacts ? [] : selectedContactIds,
            last_message_date: formatDate(lastMessageDate),
            trigger_type: 'BROKER',
            search: searchQuery || '',
            get_url: true,
            perPage: 20,
            page: 1
          };
        })();

      const response = await fetch(
        `${process.env.REACT_APP_URL_DISP}/contacts/${acontID}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao gerar CSV');
      }

      const data = await response.json();
      return data.csv_url || null;
    } catch (error) {
      console.error('Erro ao gerar CSV:', error);
      errorManagerRef.current?.showError(t('disparador.erroGerarCSV') || 'Erro ao gerar CSV');
      return null;
    }
  }, [baseSelectionType, selectedContactIds, selectAllContacts, selectedTagIds, acontID, token, filterNoInteractionDays, filterNoInteractionCustom, searchQuery, t]);

  // Função para alternar seleção de contato
  const toggleContactSelection = (contactId) => {
    if (selectAllContacts) {
      setSelectAllContacts(false);
    }
    setSelectedContactIds(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  // Função para selecionar/desselecionar todos da página atual
  const toggleSelectAll = () => {
    if (selectAllContacts) {
      setSelectAllContacts(false);
    }
    
    const currentPageContactIds = previewContacts.map(c => c.id);
    const allCurrentSelected = currentPageContactIds.every(id => selectedContactIds.includes(id));
    
    if (allCurrentSelected) {
      // Desselecionar todos da página atual
      setSelectedContactIds(prev => prev.filter(id => !currentPageContactIds.includes(id)));
    } else {
      // Selecionar todos da página atual
      setSelectedContactIds(prev => {
        const newIds = currentPageContactIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  // Efeito para buscar contatos quando os filtros mudarem
  useEffect(() => {
    if (open && ((baseSelectionType === 'filter' && filterNoInteractionDays) || (baseSelectionType === 'tag' && selectedTagIds.length > 0))) {
      const timer = setTimeout(() => {
        fetchContactsByFilter(1);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [open, baseSelectionType, filterNoInteractionDays, filterNoInteractionCustom, selectedTagIds.length, searchQuery, fetchContactsByFilter]);

  // Efeito para manter o foco no campo de pesquisa durante re-renders
  useEffect(() => {
    if (searchInputValue && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      // Restaurar foco apenas se havia texto e o foco foi perdido
      const activeElement = document.activeElement;
      if (activeElement?.tagName !== 'INPUT' && activeElement?.tagName !== 'TEXTAREA') {
        searchInputRef.current.focus();
      }
    }
  }, [previewContacts, searchInputValue]);

  const fetchTemplates = useCallback(async () => {
    setIsTemplatesLoading(true);
    try {
      // Carregar apenas a primeira página
      let firstUrl = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=false&perPage=150&page=1`;
      
      const firstResponse = await fetch(firstUrl, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!firstResponse.ok) {
        throw new Error('Erro ao buscar mensagens rápidas');
      }
      const firstData = await firstResponse.json();
      setMensagensRapidas(firstData.data || []);
      setTemplateCurrentPage(1);
      setTemplateLastPage(firstData.last_page || 1);
    } catch (error) {
      errorManagerRef.current?.showError('Erro ao buscar mensagens rápidas');
    } finally {
      setIsTemplatesLoading(false);
    }
  }, [acontID, token, channelId]);
  // Função para carregar mais templates com scroll infinito
  // eslint-disable-next-line no-unused-vars
  const handleLoadMoreTemplates = useCallback(async () => {
    if (isLoadingMoreTemplates || templateCurrentPage >= templateLastPage) {
      return; // Não carregar se já está carregando ou se chegou na última página
    }

    setIsLoadingMoreTemplates(true);
    try {
      const nextPage = templateCurrentPage + 1;
      let url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=false&perPage=150&page=${nextPage}`;
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Erro ao buscar mais mensagens rápidas');
      }
      const data = await response.json();
      
      // Adicionar novas mensagens aos existentes
      setMensagensRapidas((prevMensagens) => [...prevMensagens, ...(data.data || [])]);
      setTemplateCurrentPage(nextPage);
    } catch (error) {
      errorManagerRef.current?.showError('Erro ao carregar mais mensagens');
    } finally {
      setIsLoadingMoreTemplates(false);
    }
  }, [templateCurrentPage, templateLastPage, isLoadingMoreTemplates, acontID, channelId, token]);

  // Função para buscar templates por nome (sem debounce, será debounced no onChange)
  const searchTemplates = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      // Se o campo estiver vazio, recarregar lista padrão
      await fetchTemplates();
      return;
    }

    setIsTemplatesLoading(true);
    try {
      const url = `${process.env.REACT_APP_URL_DISP}/get-quick-messages/customer/${acontID}?channel_id=${channelId}&is_template=false&quick_message_name=${encodeURIComponent(searchTerm)}&perPage=150&page=1`;
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error('Erro ao buscar mensagens rápidas');
      }
      const data = await response.json();
      setMensagensRapidas(data.data || []);
      setTemplateCurrentPage(1);
      setTemplateLastPage(data.last_page || 1);
    } catch (error) {
      errorManagerRef.current?.showError('Erro ao buscar mensagens rápidas');
    } finally {
      setIsTemplatesLoading(false);
    }
  }, [acontID, channelId, token, fetchTemplates]);

  // Debounce da função de busca de templates (delay de 500ms)
  const debouncedSearchTemplates = useMemo(
    () => debounce((searchTerm) => {
      searchTemplates(searchTerm);
    }, 500),
    [searchTemplates]
  );

  const debouncedSearchContacts = useMemo(
    () => debounce((searchTerm) => {
      setSearchQuery(searchTerm);
    }, 500),
    []
  );

  const debouncedSearchTags = useMemo(
    () => debounce((searchTerm) => {
      searchTags(searchTerm);
    }, 500),
    [searchTags]
  );

  const handleMensagemChange = (event) => {
    const newMensagem = event.target.value;
    setMensagem(newMensagem);
    setMensagemMelhoradaPorIA(false);
    if (newMensagem && mensagemRapida) {
      setMensagemRapida('');
    }
  };

  const handleCloseModal = () => {
    // Limpar apenas a seleção da mensagem rápida, permitindo manter a mensagem editada
    setMensagemRapida('');
    setMensagem('');
    setMensagemMelhoradaPorIA(false);
    // Limpar outros estados também
    setUsuarioSelecionado('');
    setArquivoCsv(null);
    setArquivoCsvUrl('');
    setImagem(null);
    setImagemUrl('');
    setEmail('');
    setEtiqueta('');
    setSelectedBot('');
    setTags([]);
    setTagsCurrentPage(1);
    setTagsLastPage(1);
    setTagSearchTerm('');
    setTagSearchInputValue('');
    setSelectedTags([]);
    setIsCsvUploaded(false);
    setIsImageUploaded(false);
    setFormErrors({});
    setSearchQuery('');
    setSearchInputValue('');
    setActiveStep(0);
    onClose();
  };

  useEffect(() => {
    if (open) {
      const now = new Date();
      setSelectedBot("");
      setTagSearchTerm('');
      setTagSearchInputValue('');
      // Se for operador apenas, pré-seleciona o usuário diretamente
      if (isOperadorOnly && currentUserId) {
        setUsuarioSelecionado(currentUserId);
      } else {
        // Caso contrário, busca a lista de usuários
        fetchUsuarios();
      }
      fetchTemplates();
      fetchBotsData();
      fetchTagsData('');
      setSchedule_datetime(formatDate(now));
      setActiveStep(0);
    }
  }, [open, fetchUsuarios, fetchBotsData, fetchTemplates, fetchTagsData, isOperadorOnly, currentUserId]);

  const stepLabels = [
    t('disparador.stepDispatchData'),
    t('disparador.stepContactBase'),
    t('disparador.stepFinalSettings')
  ];

  const validateCurrentStep = () => {
    const currentDate = new Date();
    const maxDate = new Date(currentDate);
    maxDate.setDate(maxDate.getDate() + 6);
    const selectedDate = new Date(schedule_datetime);
    const stepErrors = {};

    if (activeStep === 0) {
      if (!isOperadorOnly && !usuarioSelecionado) stepErrors.usuario = t('validation.userRequired');
      if (!schedule_datetime) stepErrors.date = t('validation.dateRequired');
      if (!mensagem && !mensagemRapida) stepErrors.mensagem = t('validation.messageRequired');
      if (selectedDate > maxDate) stepErrors.date = t('validation.dateExceedsLimit');
    }

    if (activeStep === 1) {
      if (baseSelectionType === 'csv') {
        if (!arquivoCsvUrl) stepErrors.csv = t('validation.csvRequired');
      } else if (baseSelectionType === 'filter' || baseSelectionType === 'tag') {
        if (!selectAllContacts && selectedContactIds.length === 0) {
          stepErrors.contacts = t('disparador.selecioneContatos') || 'Selecione pelo menos um contato';
        }
        if (baseSelectionType === 'tag' && selectedTagIds.length === 0) {
          stepErrors.tag = t('disparador.selecioneTag') || 'Selecione uma tag';
        }
      }
    }

    setFormErrors((prev) => ({ ...prev, ...stepErrors }));
    return Object.keys(stepErrors).length === 0;
  };

  const handleNextStep = () => {
    if (!validateCurrentStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, stepLabels.length - 1));
  };

  const handleBackStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <>
      <ErrorManager ref={errorManagerRef} />
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="modal">
          {showSuccessMessage && <SuccessScreen />}
          <form className="modal-content" onSubmit={handleFormSubmit}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box flexGrow={1} textAlign="center">
                <Typography variant="h6" component="h2" style={{ fontWeight: 'normal', margin: 0 }}>
                  {t('disparador.criarCampanha')}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseModal}>
                <CloseIcon />
              </IconButton>
            </Box>
            <div className="input-container">
              <Typography variant="subtitle1" sx={{ my: 2 }}>
                {t('disparador.canalSelecionado')} <strong>{channelName}</strong>
              </Typography>
            </div>

            <BrokerStepIndicator activeStep={activeStep} stepLabels={stepLabels} />

            <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
            <div className="input-container">
              <Autocomplete
                fullWidth
                options={mensagensRapidas}
                getOptionLabel={(option) => option.tag || ''}
                value={mensagensRapidas.find(t => t.id === mensagemRapida) || null}
                onChange={(event, newValue) => {
                  setMensagemRapida(newValue ? newValue.id : '');
                  if (newValue) {
                    setMensagem('');
                    setMensagemMelhoradaPorIA(false);
                    setImagem(null);
                    setImagemUrl('');
                    setIsImageUploaded(false);
                  }
                }}
                onInputChange={(event, value) => {
                  debouncedSearchTemplates(value);
                }}
                open={templateAutocompleteOpen}
                onOpen={() => setTemplateAutocompleteOpen(true)}
                onClose={() => setTemplateAutocompleteOpen(false)}
                loading={isTemplatesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('disparador.labelMensagem')}
                    margin="normal"
                    placeholder={t('disparador.digiteBuscar')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isTemplatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={t('disparador.naoSelecionado')}
                ListboxProps={{
                  style: { maxHeight: '200px' },
                }}
              />
            </div>
            {!mensagemRapida && (
              <div className="input-container">
                <TextField
                  fullWidth
                  label={t('disparador.mensagem')}
                  margin="normal"
                  multiline
                  rows={4}
                  value={mensagem}
                  onChange={handleMensagemChange}
                  required={!mensagemRapida}
                  error={!!formErrors.mensagem}
                  FormHelperTextProps={{ component: 'div' }}
                  helperText={
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box component="span" sx={{ color: formErrors.mensagem ? 'error.main' : 'text.secondary' }}>
                        {formErrors.mensagem || t('disparador.digiteMsg')}
                      </Box>
                      <ImproveTextWithAI
                        text={mensagem}
                        onApply={(improvedText) => setMensagem(improvedText)}
                        disabled={!mensagem.trim()}
                        isAlreadyImproved={mensagemMelhoradaPorIA}
                        onAiApplied={() => setMensagemMelhoradaPorIA(true)}
                        onAiUndone={() => setMensagemMelhoradaPorIA(false)}
                        customerId={acontID}
                        token={token}
                      />
                    </Box>
                  }
                />
              </div>
            )}
            {!mensagemRapida && (
              <div
                className={`upload-container ${isImageDragActive ? 'drag-active' : ''}`}
                onDragOver={handleImageDragOver}
                onDragLeave={handleImageDragLeave}
                onDrop={handleImageDrop}
              >
              <Button variant="contained" component="label">
                {isImageLoading ? <CircularProgress size={24} style={{ color: 'white' }} /> : t('disparador.adicionarImagem')}
                <input
                  type="file"
                  accept="image/jpeg, image/png, image/gif, application/pdf"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {isImageUploaded && (
                <div className="file-preview" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  marginTop: '8px',
                  maxWidth: '100%'
                }}>
                  <a 
                    href={imagemUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      color: '#1976d2',
                      textDecoration: 'none',
                      marginRight: '8px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                      maxWidth: 'calc(100% - 80px)'
                    }}
                  >
                    {imagem.name}
                  </a>
                  <div className="file-actions" style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px', 
                    flexShrink: 0,
                    minWidth: '72px'
                  }}>
                    <IconButton onClick={handleRemoveImage} size="small" className="remove-button">
                      <DeleteIcon />
                    </IconButton>
                    <CheckCircleIcon className="check-icon" />
                  </div>
                </div>
              )}
              </div>
            )}
            {!isOperadorOnly && (
              <div className="input-container">
                <FormControl fullWidth margin="normal" required>
                  <Autocomplete
                    fullWidth
                    options={usuarios}
                    getOptionLabel={(option) => option.name}
                    value={usuarioSelecionado ? usuarios.find(user => user.id === usuarioSelecionado) : null}
                    onChange={(event, newValue) => setUsuarioSelecionado(newValue ? newValue.id : '')}
                    onInputChange={handleInputChangeDynamic}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('disparador.remetenteCampanha')}
                        margin="normal"
                        fullWidth
                        error={!!formErrors.usuario}
                        helperText={formErrors.usuario}
                      />
                    )}
                    noOptionsText={t('disparador.digiteBuscar')}
                  />
                </FormControl>
              </div>
            )}
            <div className="input-container">
              <TextField
                fullWidth
                label={t('disparador.dataEnvio')}
                margin="normal"
                type="datetime-local"
                value={schedule_datetime}
                onChange={(event) => {
                  const currentDate = new Date();
                  const maxDate = new Date(currentDate);
                  maxDate.setDate(maxDate.getDate() + 6);
                  const selectedDate = new Date(event.target.value);
                  if (selectedDate > maxDate) {
                    setFormErrors({ date: 'A data ultrapassa o período permitido para agendamentos' });
                  } else {
                    setFormErrors(prev => ({ ...prev, date: '' }));
                  }
                  setSchedule_datetime(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                required
                error={!!formErrors.date}
                helperText={formErrors.date}
              />
            </div>
            </Box>
            
            {/* Seleção de tipo de base */}
            <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
            <div className="input-container">
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                {t('disparador.selecaoBase') || 'Seleção de Base'}
              </Typography>
              <RadioGroup
                value={baseSelectionType}
                onChange={(e) => {
                  setBaseSelectionType(e.target.value);
                  setFormErrors({});
                  if (e.target.value !== 'filter') {
                    setSelectedContactIds([]);
                    setPreviewContacts([]);
                    setSelectAllContacts(true);
                  }
                  if (e.target.value !== 'tag') {
                    setSelectedTags([]);
                  }
                }}
              >
                <FormControlLabel 
                  value="csv" 
                  control={<Radio />} 
                  label={t('disparador.uploadPlanilha') || 'Upload de planilha (.csv/.xlsx)'} 
                />
                <FormControlLabel 
                  value="filter" 
                  control={<Radio />} 
                  label={t('disparador.baseFiltroInteracao') || 'Base por Filtro de Interação'} 
                />
                <FormControlLabel 
                  value="tag" 
                  control={<Radio />} 
                  label={t('disparador.baseFiltroEtiqueta') || 'Base por filtro de etiqueta'} 
                />
              </RadioGroup>
            </div>

            {baseSelectionType === 'tag' && (
              <div className="input-container" style={{ marginTop: '16px' }}>
                <Autocomplete
                  fullWidth
                  multiple
                  disableCloseOnSelect
                  options={tagOptions}
                  loading={isTagsLoading}
                  inputValue={tagSearchInputValue}
                  getOptionLabel={(option) => option.name || ''}
                  value={selectedTags}
                  getOptionDisabled={(option) => Boolean(option?.isLoadingOption)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setSelectedTags((newValue || []).filter((item) => !item?.isLoadingOption));
                    setTagSearchInputValue('');
                    debouncedSearchTags('');
                  }}
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setTagSearchInputValue(value || '');
                      debouncedSearchTags(value || '');
                    }
                  }}
                  renderOption={renderTagOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecionar tag"
                      margin="normal"
                      error={!!formErrors.tag}
                      helperText={formErrors.tag || 'Escolha a tag usada para carregar os contatos'}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isTagsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText={isTagsLoading ? 'Carregando tags...' : 'Nenhuma tag encontrada'}
                  ListboxProps={{
                    style: { maxHeight: '200px' },
                    onScroll: handleTagsListScroll,
                  }}
                />
              </div>
            )}
            
            {/* Upload de CSV - mostrado apenas quando baseSelectionType === 'csv' */}
            {baseSelectionType === 'csv' && (
              <>
                <div
                  className={`upload-container ${isCsvDragActive ? 'drag-active' : ''}`}
                  onDragOver={handleCsvDragOver}
                  onDragLeave={handleCsvDragLeave}
              onDrop={handleCsvDrop}
              style={{
                border: '2px dashed #ccc',
                borderColor: formErrors.csv ? 'red' : '#ccc',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <CloudUploadIcon style={{ marginRight: '8px', color: '#5a5a5a' }} />
                <Typography variant="body1" style={{ marginRight: '16px' }}>
                  {t('disparador.adicionarContatos')}
                </Typography>
                <Button variant="contained" component="label" color="primary">
                  {isLoading ? <CircularProgress size={24} style={{ color: 'white' }} /> : t('disparador.escolherArquivo')}
                  <input
                    type="file"
                    name="csvFile"
                    accept=".csv,.xlsx"
                    hidden
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                </Button>
              </Box>
              {isCsvUploaded && (
                <div className="file-preview" style={{ marginTop: '8px' }}>
                  <a href={arquivoCsvUrl} download={arquivoCsv ? arquivoCsv.name : 'arquivo.csv'}>
                    {arquivoCsv ? arquivoCsv.name : 'arquivo.csv'}
                  </a>
                  <div className="file-actions" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '8px' }}>
                    <IconButton onClick={handleRemoveCsv} size="small" className="remove-button">
                      <DeleteIcon />
                    </IconButton>
                    <CheckCircleIcon className="check-icon" color="success" />
                  </div>
                </div>
              )}

              {/* Informação de Validação */}

              {formErrors.csv && (
                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: '8px' }}>
                  {t('disparador.obrigatorioCSV')}
                </Typography>
              )}
            </div>
            <Box display="flex" justifyContent="flex-start" alignItems="center" mt={1}>
              <Typography variant="caption" color="textSecondary" mr={2}>
                {t('disparador.baixeModelo')}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  const language = localStorage.getItem('i18nextLng') || 'en';
                  const modelLinks = {
                    en: 'https://docs.google.com/spreadsheets/d/1BXYT-zGazRkVprlcAdhxGLRYj0XKZnfAQrmlykR8BhY/copy',
                    es: 'https://docs.google.com/spreadsheets/d/1kFJ9c0eKoL7tHtKM2BxSDtRUe4smN9-zP_1C0mqzod0/copy',
                    'pt-BR': 'https://docs.google.com/spreadsheets/d/1hZvUOtlVa_ydT_EZmoij2a0XYpmiGCBD3zkgWZX-sdw/copy'
                  };
                  const link = modelLinks[language] || modelLinks['en'];
                  window.open(link, '_blank');
                }}
              >
                {t('disparador.modeloPlanilha')}
              </Button>
            </Box>
              </>
            )}
            
            {/* Filtros de Interação / Tag */}
            {(baseSelectionType === 'filter' || baseSelectionType === 'tag') && (
              <Box sx={{ border: '1px solid #ddd', borderRadius: '8px', p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  {baseSelectionType === 'tag' ? 'Contatos das tags selecionadas' : (t('disparador.filtrosInteracao') || 'Filtros de Interação')}
                </Typography>
                {baseSelectionType === 'filter' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('disparador.semInteracaoHa') || 'Sem interação há:'}
                      <Tooltip title={t('disparador.dicaSemInteracao') || 'Considera a última mensagem recebida do contato'} placement="top" arrow>
                        <span style={{ cursor: 'pointer', color: '#1976d2', marginLeft: '4px' }}>ⓘ</span>
                      </Tooltip>
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Select
                        fullWidth
                        value={filterNoInteractionDays}
                        onChange={(e) => setFilterNoInteractionDays(e.target.value)}
                      >
                        <MenuItem value="7">7 {t('disparador.dias') || 'dias'}</MenuItem>
                        <MenuItem value="15">15 {t('disparador.dias') || 'dias'}</MenuItem>
                        <MenuItem value="30">30 {t('disparador.dias') || 'dias'}</MenuItem>
                        <MenuItem value="custom">{t('disparador.personalizado') || 'Personalizado'}</MenuItem>
                      </Select>
                      {filterNoInteractionDays === 'custom' && (
                        <TextField
                          type="number"
                          placeholder={t('disparador.dias') || 'Dias'}
                          value={filterNoInteractionCustom}
                          onChange={(e) => setFilterNoInteractionCustom(e.target.value)}
                          sx={{ width: '150px' }}
                        />
                      )}
                    </Box>
                  </Box>
                )}
                
                {/* Pré-visualização de contatos */}
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {previewTotalContacts} {t('disparador.contatosEncontrados') || 'contatos encontrados'}
                    </Typography>
                    {(baseSelectionType === 'filter' || baseSelectionType === 'tag') && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {selectedContactIds.length} {t('disparador.contatosSelecionados') || 'contatos selecionados'}
                      </Typography>
                    )}
                    
                    {/* Campo de Pesquisa */}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('disparador.pesquisarContato') || 'Pesquisar por nome ou número'}
                      value={searchInputValue}
                      onChange={(e) => {
                        setSearchInputValue(e.target.value);
                        debouncedSearchContacts(e.target.value);
                      }}
                      inputRef={searchInputRef}
                      sx={{ mb: 2 }}
                      disabled={isLoadingPreview}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>🔍</Box>
                        ),
                      }}
                    />

                    {isLoadingPreview ? (
                      <Box display="flex" justifyContent="center" alignItems="center" py={4} sx={{ minHeight: 200 }}>
                        <CircularProgress />
                      </Box>
                    ) : previewContacts.length > 0 ? (
                      <>
                        {(baseSelectionType === 'filter' || baseSelectionType === 'tag') && (
                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f5f5f5', p: 1.5, borderRadius: 1 }}>
                            <Checkbox
                              checked={selectAllContacts}
                              onChange={(e) => {
                                setSelectAllContacts(e.target.checked);
                                if (e.target.checked) {
                                  setSelectedContactIds([]);
                                }
                              }}
                              color="primary"
                              disabled={isLoadingPreview}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {t('disparador.selecionarTodos') || 'Selecionar todos'}
                            </Typography>
                          </Box>
                        )}
                        
                        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                {(baseSelectionType === 'filter' || baseSelectionType === 'tag') && (
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selectAllContacts || (previewContacts.length > 0 && previewContacts.every(c => selectedContactIds.includes(c.id)))}
                                      onChange={toggleSelectAll}
                                      indeterminate={!selectAllContacts && previewContacts.some(c => selectedContactIds.includes(c.id)) && !previewContacts.every(c => selectedContactIds.includes(c.id))}
                                      disabled={selectAllContacts || isLoadingPreview}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>{t('disparador.nome') || 'Nome'}</TableCell>
                                <TableCell>{t('disparador.telefone') || 'Telefone'}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {previewContacts.map((contact) => (
                                <TableRow key={contact.id}>
                                  {(baseSelectionType === 'filter' || baseSelectionType === 'tag') && (
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        checked={selectAllContacts || selectedContactIds.includes(contact.id)}
                                        onChange={() => toggleContactSelection(contact.id)}
                                        disabled={selectAllContacts || isLoadingPreview}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell>{contact.name || '-'}</TableCell>
                                  <TableCell>{contact.phone || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        {previewLastPage > 1 && (
                          <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination 
                              count={previewLastPage} 
                              page={previewCurrentPage} 
                              onChange={(e, page) => fetchContactsByFilter(page)}
                              color="primary"
                              disabled={isLoadingPreview}
                            />
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {searchInputValue 
                            ? 'Nenhum contato encontrado com o termo pesquisado'
                            : 'Nenhum contato encontrado com os filtros selecionados'
                          }
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {formErrors.contacts && (
                    <Typography variant="caption" color="error">
                      {formErrors.contacts}
                    </Typography>
                  )}
                </>
              </Box>
            )}
            </Box>
            
            <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
            <div className="input-container">
              <TextField
                fullWidth
                label={t('disparador.email')}
                margin="normal"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  if (!newEmail) {
                    setFormErrors((prev) => ({ ...prev, email: t('validation.emailRequired') }));
                  } else if (!validateEmail(newEmail)) {
                    setFormErrors((prev) => ({ ...prev, email: t('validation.emailInvalid') }));
                  } else {
                    setFormErrors((prev) => ({ ...prev, email: '' }));
                  }
                }}
                required
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </div>
            <div className="input-container">
              <Box display="flex" alignItems="center" gap={1}>
                <Switch
                  checked={adicionarEtiqueta}
                  onChange={(e) => setAdicionarEtiqueta(e.target.checked)}
                  color="primary"
                />
                <Typography variant="body2">{t('disparador.adicionarEtiqueta')}</Typography>
                <Tooltip title={t('disparador.dicaEtiqueta')} placement="top" arrow>
                  <span style={{ cursor: 'pointer', color: '#1976d2' }}>ⓘ</span>
                </Tooltip>
              </Box>
            </div>
            {adicionarEtiqueta && (
              <div className="input-container">
                <TextField
                  fullWidth
                  label={t('disparador.nomeEtiqueta')}
                  margin="normal"
                  value={etiqueta}
                  onChange={(e) => setEtiqueta(e.target.value)}
                />
              </div>
            )}
            <div className="input-container">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{t('disparador.acionarBot')}</Typography>
                <Tooltip title={t('disparador.dicaBot')} placement="top" arrow>
                  <span style={{ cursor: 'pointer', color: '#1976d2' }}>ⓘ</span>
                </Tooltip>
              </Box>
              <Select
                fullWidth
                value={fecharChat}
                onChange={(e) => setFecharChat(e.target.value)}
                displayEmpty
                margin="normal"
                required
              >
                <MenuItem value={true}>{t('disparador.simacionarBot')}</MenuItem>
                <MenuItem value={false}>{t('disparador.naoacionarBot')}</MenuItem>
              </Select>
            </div>
            {fecharChat && (
              <div className="input-container">
                <FormControl fullWidth margin="normal">
                  <Select
                    labelId="select-bot-label"
                    id="select-bot"
                    fullWidth
                    value={selectedBot || ""}
                    onChange={(e) => setSelectedBot(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      {bots.length === 0 ? t('disparador.nenhumBotDisp') : t('disparador.escolheBots')}
                    </MenuItem>
                    {bots.map((bot) => (
                      <MenuItem key={bot.legacy_flow_id} value={bot.legacy_flow_id}>{bot.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}
            <div className="input-container">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{t('disparador.evitarDuplicadas')}</Typography>
                <Tooltip title={t('disparador.dicaDuplicadas')} placement="top" arrow>
                  <span style={{ cursor: 'pointer', color: '#1976d2' }}>ⓘ</span>
                </Tooltip>
              </Box>
              <Select
                fullWidth
                value={contatosDuplicados}
                onChange={(e) => setContatosDuplicados(e.target.value)}
                displayEmpty
                margin="normal"
                required
              >
                <MenuItem value={true}>{t('disparador.simEvitar')}</MenuItem>
                <MenuItem value={false}>{t('disparador.nao')}</MenuItem>
              </Select>
            </div>
            <div className="input-container">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{t('disparador.adicionarDDI')}</Typography>
                <Tooltip title={t('disparador.dicaDDI')} placement="top" arrow>
                  <span style={{ cursor: 'pointer', color: '#1976d2' }}>ⓘ</span>
                </Tooltip>
              </Box>
              <Select
                fullWidth
                value={entryInternationalPhone}
                onChange={(e) => setEntryInternationalPhone(e.target.value)}
                displayEmpty
                margin="normal"
                required
              >
                <MenuItem key="add-ddi" value={false}>{t('disparador.simAdicionarDDI')}</MenuItem>
                <MenuItem key="keep-as-is" value={true}>{t('disparador.naoManter')}</MenuItem>
              </Select>
            </div>
            </Box>

            <div className="button-container">
              <Box display="flex" justifyContent="space-between" gap={1.5} width="100%" flexDirection={{ xs: 'column-reverse', sm: 'row' }}>
                <Button
                  variant="outlined"
                  onClick={handleBackStep}
                  disabled={activeStep === 0 || isSubmitting || isValidating}
                  fullWidth
                >
                  {t('disparador.stepBack')}
                </Button>

                {activeStep < stepLabels.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNextStep}
                    disabled={isSubmitting || isValidating}
                    fullWidth
                  >
                    {t('disparador.stepNext')}
                  </Button>
                ) : (
                  <Button variant="contained" color="primary" type="submit" disabled={isSubmitting || isValidating} fullWidth>
                    {isValidating ? (
                      <>
                        <CircularProgress size={24} style={{ color: 'white', marginRight: '8px' }} />
                        {t('disparador.validandoContatos')}
                      </>
                    ) : isSubmitting ? (
                      <CircularProgress size={24} style={{ color: 'white' }} />
                    ) : (
                      t('disparador.enviarDisparo')
                    )}
                  </Button>
                )}
              </Box>
            </div>
            {showSuccessMessage && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={2} className="success-message">
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle1" className="success-text">{t('disparador.dadosEnviadosSucesso')}</Typography>
              </Box>
            )}
          </form>
        </div>
      </Modal>

      {/* Modal de Confirmação de Contatos */}
      <Modal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        aria-labelledby="confirmation-modal-title"
        aria-describedby="confirmation-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100% - 16px)', sm: '500px' },
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: { xs: 2, sm: 3 },
            maxHeight: { xs: '92vh', sm: '85vh' },
            overflowY: 'auto',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography id="confirmation-modal-title" variant="h6" component="h2">
              {t('disparador.confirmarEnvioCampanha')}
            </Typography>
            <IconButton onClick={() => setConfirmationModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {validationData && (
            <>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Resumo da validação da sua lista de contatos:
              </Typography>

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  📋 <strong>{t('disparador.totalContatos')}</strong> {validationData.total_sent || 0}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                  ✅ <strong>{t('disparador.contatosElegibles')}</strong> {validationData.total_eligible || 0}
                </Typography>
              </Box>

              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Mensagem: <strong>{mensagensRapidas.find((item) => item.id === mensagemRapida)?.tag || (mensagem ? 'Texto manual' : '-')}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: 1,
                      bgcolor: '#eceff1',
                      color: 'text.secondary',
                      fontWeight: 600,
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {mensagemRapida ? 'Mensagem Rápida' : 'Texto Livre'}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={1.5} flexDirection={{ xs: 'column-reverse', sm: 'row' }}>
                <Button
                  variant="outlined"
                  onClick={() => setConfirmationModalOpen(false)}
                  fullWidth
                >
                  {t('disparador.cancelar')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmSend}
                  disabled={isSubmitting}
                  fullWidth
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} style={{ color: 'white' }} />
                  ) : (
                    t('disparador.confirmarEnviar')
                  )}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Modal de Erro de Validação */}
      {errorData && errorModalOpen && (
        <Modal
          open={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          aria-labelledby="error-modal-title"
          aria-describedby="error-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100% - 16px)', sm: '500px' },
            bgcolor: 'background.paper',
            border: '2px solid #f44336',
            borderRadius: 2,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            maxHeight: { xs: '92vh', sm: '80vh' },
            overflowY: 'auto'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography id="error-modal-title" variant="h6" component="h2" sx={{ 
                color: 'error.main',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center'
              }}>
                ⚠️ {t('disparador.erroValidacaoArquivo')}
              </Typography>
              <IconButton onClick={() => setErrorModalOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {errorData && (
              <>
                <Box sx={{ bgcolor: '#ffebee', p: 2, borderRadius: 1, mb: 3, border: '1px solid #ffcdd2' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: 'error.main' }}>
                    {t('disparador.detalhesErro')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    {errorData.detail || 'Erro não especificado'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setErrorModalOpen(false)}
                    sx={{ minWidth: '120px' }}
                  >
                    Entendi
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      )}
    </>
  );
};

export default BrokerModal;